import { Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { masterDataRoutes } from './master-data/master-data.routes';
import { authGuard, LogoutComponent } from '@doka/identity-login';
import { langSyncGuard } from '@doka/i18next';
import { masterDataGuard } from './master-data/master-data.guard';

/**
 * Custom URL matcher that consolidates all OverviewPage URL patterns into a single route definition.
 * This prevents Angular from destroying and recreating the component when navigating between
 * different hierarchy levels (division, region, country, branch) within the overview.
 */
export function overviewMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length === 0 || segments[0].path !== 'plan') {
    return null;
  }

  // /plan
  if (segments.length === 1) {
    return { consumed: segments, posParams: {} };
  }

  // /plan/division/...
  if (segments[1].path === 'division') {
    // /plan/division/:divisionId
    if (segments.length === 3) {
      return { consumed: segments, posParams: { divisionId: segments[2] } };
    }

    // /plan/division/:divisionId/branch/:branchId
    if (segments.length === 5 && segments[3].path === 'branch') {
      return { consumed: segments, posParams: { divisionId: segments[2], branchId: segments[4] } };
    }

    // /plan/division/:divisionId/:hierarchyType/:entityId
    if (segments.length === 5) {
      return {
        consumed: segments,
        posParams: { divisionId: segments[2], hierarchyType: segments[3], entityId: segments[4] },
      };
    }

    return null;
  }

  // /plan/branch/:branchId
  if (segments[1].path === 'branch' && segments.length === 3) {
    return { consumed: segments, posParams: { branchId: segments[2] } };
  }

  // /plan/:hierarchyType/:entityId (region or country without division)
  if (segments.length === 3 && (segments[1].path === 'region' || segments[1].path === 'country')) {
    return { consumed: segments, posParams: { hierarchyType: segments[1], entityId: segments[2] } };
  }

  // No match — let Angular fall through to plan/:planId
  return null;
}

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'my-tasks',
  },
  {
    path: 'my-tasks',
    loadComponent: () => import('./my-tasks/my-tasks.page').then((m) => m.MyTasksPage),
    canActivate: [authGuard, langSyncGuard],
  },
  {
    matcher: overviewMatcher,
    loadComponent: () => import('./overview/overview.page').then((m) => m.OverviewPage),
    canActivate: [authGuard, langSyncGuard],
  },
  {
    path: 'plan/:planId',
    loadComponent: () => import('./plan/plan.page').then((m) => m.PlanPage),
    canActivate: [authGuard, langSyncGuard],
  },
  {
    path: 'master-data',
    ...masterDataRoutes,
    loadComponent: () => import('./master-data/master-data.page').then((m) => m.MasterDataPage),
    canActivate: [authGuard, langSyncGuard, masterDataGuard],
  },
  {
    path: 'forbidden',
    loadComponent: () => import('./forbidden/forbidden.page').then((m) => m.ForbiddenPage),
  },
  {
    path: 'logout',
    component: LogoutComponent,
  },
  {
    path: '**',
    redirectTo: 'my-tasks',
  },
];
